import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    BlackoutFragment,
    Blackouts_Insert_Input,
    UpdateBlackoutMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    TalentAdminBlackoutCreateFragment, 
    TalentAdminBlackoutCreateSchema, 
    TalentAdminBlackoutUpdateFragment, 
    TalentAdminBlackoutUpdateSchema 
} from "src/components/talent/blackouts/TalentAdminBlackoutSchemas"
import { extractCurrentItemData } from "src/utils/forms"

type FormPayloadType = TalentAdminBlackoutCreateFragment

let initialState: FormPayloadType = {
    description: "Unavailable",
    start_at: "",
    end_at: ""
};

export const CreateTalentAdminBlackoutDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().blackouts;
    const { loading, errors } = useAppState().blackouts;
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Blackouts_Insert_Input) => {
        return createItem({ ...payload })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, BlackoutFragment, string>
                autoOpen={false}
                formSchema={TalentAdminBlackoutCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateTalentAdminBlackoutAction = () => {
    return (
        <PageDrawerAction<BlackoutFragment>
            title='Add Blackout Dates'
            action={CreateTalentAdminBlackoutDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: TalentAdminBlackoutUpdateFragment = {
    description: "",
    start_at: "",
    end_at: ""
};

export const UpdateTalentAdminBlackoutDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().blackouts;
    const { currentItem, loading, errors } = useAppState().blackouts;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: TalentAdminBlackoutUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<TalentAdminBlackoutUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateBlackoutMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<TalentAdminBlackoutUpdateFragment, BlackoutFragment, string>
                autoOpen={false}
                formSchema={TalentAdminBlackoutUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateTalentAdminBlackoutAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().blackouts;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.blackouts.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<BlackoutFragment>
            title='Edit Blackout Dates'
            action={UpdateTalentAdminBlackoutDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditTalentAdminBlackoutAction = () => {
    return (
        <PageDrawerAction<BlackoutFragment>
            title='Edit Blackout Dates'
            action={UpdateTalentAdminBlackoutDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}